import app from 'flarum/forum/app';
import IndexPage from 'flarum/forum/components/IndexPage';
import LogInModal from 'flarum/forum/components/LogInModal';
import Placeholder from "flarum/common/components/Placeholder";
import extractText from 'flarum/common/utils/extractText';
import Tooltip from 'flarum/common/components/Tooltip';
import Button from 'flarum/common/components/Button';
import type Client from 'src/common/models/Client';
import type Scope from 'src/common/models/Scope';
import LoadingIndicator from 'flarum/common/components/LoadingIndicator';

interface AuthorizeRequestParams {
  response_type: string,
  client_id: string,
  redirect_uri: string,
  state: string,
  scope: string,
}

export default class AuthorizePage extends IndexPage {
  params: AuthorizeRequestParams | null = null;
  client: Client | null = null;
  clientScopes: Scope[] = [];
  loading = true;
  submitting = false;

  oninit(vnode: any) {
    super.oninit(vnode);

    if (!app.session.user) return;

    const rawParams = m.route.param();

    if (rawParams.client_id == null || rawParams.response_type == null || rawParams.redirect_uri == null) {
      m.route.set('/');
      return;
    }

    const params = {
      client_id: rawParams.client_id,
      response_type: rawParams.response_type,
      redirect_uri: rawParams.redirect_uri,
      state: rawParams.state,
      scope: rawParams.scope
    };
    this.params = params;

    const scopesPromise = app.store.find('oauth-scopes');
    app.store.find('oauth-clients', params.client_id).then(response => {
      const clients = response as unknown as Client[];
      if (clients.length === 0) {
        m.route.set('/');
        return;
      }

      this.client = clients[0];
      let uris = [this.client.redirect_uri()];
      if (uris[0].indexOf(' ') > -1) {
        uris = uris[0].split(' ');
      }

      if ((app.forum.attribute('rhodes-island-oauth-center.require_exact_redirect_uri') && uris.indexOf(params.redirect_uri) == -1) &&
        (!app.forum.attribute('rhodes-island-oauth-center.allow_implicit') && params.response_type == 'token') &&
        (app.forum.attribute('rhodes-island-oauth-center.enforce_state') && rawParams.enforce_state == null)) {
        m.route.set('/');
      }

      scopesPromise.then(scopes => {
        const wantedScopes = [];

        const paramScopes = params.scope as string;
        if (paramScopes) {
          wantedScopes.push(...paramScopes.split(" "));
        }

        const clientScopes = this.client!!.scope();
        if (clientScopes) {
          wantedScopes.push(...clientScopes.split(" "));
        }

        const finalScopes = [];

        const definedScopes = (scopes as unknown as Scope[]);
        for (const definedScope of definedScopes) {
          if (definedScope.is_default() || wantedScopes.indexOf(definedScope.scope()) != -1) {
            finalScopes.push(definedScope);
          }
        }

        this.clientScopes = finalScopes;
        this.loading = false;
      });
    });
  }

  setTitle() {
    app.setTitle(extractText(app.translator.trans('rhodes-island-oauth-center.forum.page.title.authorize')));
    app.setTitleCount(0);
  }

  oncreate(vnode: any): void {
    super.oncreate(vnode);

    if (!app.session.user) {
      setTimeout(() => app.modal.show(LogInModal)); // make sure mithril wont get double redraw
    }
  }

  view() {
    if (!app.session.user) {
      return <Placeholder>{app.translator.trans("rhodes-island-oauth-center.forum.authorize.not_logged_in")}</Placeholder>;
    }
    if (this.loading) {
      return <LoadingIndicator></LoadingIndicator>;
    }

    const user = app.session.user;
    const clientHome = this.client!!.client_home();
    const clientIcon = this.client!!.client_icon();
    return (
      <div className="AuthorizePage">
        <div className="container">
          <div class="OAuth-Header">
            <h2>{app.translator.trans("rhodes-island-oauth-center.forum.authorize.title")}</h2>
            <p>{app.translator.trans('rhodes-island-oauth-center.forum.authorize.description')}</p>
          </div>
          <div class="OAuth-Body">
            <div className="OAuth-User">
              <img className="OAuth-User-Avatar" src={user.attribute("avatarUrl")}/>
              <div className="OAuth-User-Info">
                <h4>{user.attribute("displayName")}</h4>
                <small>{user.attribute("email")}</small>
              </div>
            </div>
            <div className="OAuth-Details">
              <div className="OAuth-App">
                <p>{app.translator.trans('rhodes-island-oauth-center.forum.authorize.app_text')}</p>
                {(clientHome ? <Tooltip text={app.translator.trans('rhodes-island-oauth-center.forum.authorize.to_app_homepage', { name: this.client!!.client_name() })}>
                  <a href={clientHome} target="_blank" className="OAuth-App-Info">
                  {clientIcon ? <img src={clientIcon} class="OAuth-App-Icon"/> : <i class="OAuth-App-Icon fas fa-5x fa-cube"></i>}
                    <p>{this.client!!.client_name()}</p>
                    <small>{this.client!!.client_desc()}</small>
                  </a>
                </Tooltip> : <div className="OAuth-App-Info">
                    {clientIcon ? <img src={clientIcon} class="OAuth-App-Icon"/> : <i class="OAuth-App-Icon fas fa-5x fa-cube"></i>}
                    <p>{this.client!!.client_name()}</p>
                    <small>{this.client!!.client_desc()}</small>
                </div>)}
              </div>
              <div class="OAuth-Scopes">
                <p>{app.translator.trans('rhodes-island-oauth-center.forum.authorize.scope_text')}</p>
                {
                  this.clientScopes.length > 0 && this.clientScopes.map(scope => {
                    return <div class="OAuth-Scope">
                      {
                        (scope.scope_icon().indexOf('fa-') > -1) ?
                          <i class={"OAuth-ScopeIcon fa-3x " + scope.scope_icon()}
                            style="margin-left:2px;color:#000"></i> :
                          <img class="OAuth-ScopeIcon" src={scope.scope_icon()} />
                      }
                      <div class="OAuth-ScopeDetail">
                        <h4>
                          {scope.scope_name()}
                        </h4>
                        <small>
                          {
                            scope.scope_desc()
                              .replace('{client_name}', this.client!!.client_name())
                              .replace('{user}', user.attribute('displayName'))
                          }
                        </small>
                      </div>
                    </div>
                  })
                }
              </div>
            </div>
              {this.submitting ? <LoadingIndicator/> : 
              <div class="OAuth-Action">
                <Button className="Button Button--primary Button--block" onclick={() => this.submit(true)}>
                  {app.translator.trans('rhodes-island-oauth-center.forum.authorize.agree')}
                </Button>
                <Button className="Button Button--link Button--block" onclick={() => this.submit(false)}>
                  {app.translator.trans('rhodes-island-oauth-center.forum.authorize.deny')}
                </Button>
              </div>
              }
          </div>
        </div>
      </div>
    );
  }

  submit(authorized: boolean) {
    this.submitting = true;
    const form = {
      response_type: this.params!!.response_type,
      client_id: this.params!!.client_id,
      redirect_uri: this.params!!.redirect_uri,
      state: this.params!!.state,
      scope: this.params!!.scope,
      is_authorized: authorized,
    };
    if (app.forum.attribute('rhodes-island-oauth-center.use_redirect_authorize')) {
      const formEl = document.createElement("form");
      formEl.style.display = "none";
      formEl.action = "/oauth/authorize/redirect";
      formEl.method = "POST";
      for (const k in form) {
        const el = document.createElement("input");
        el.name = k;
        el.value = (form as any)[k];
        formEl.appendChild(el);
      }
      document.body.appendChild(formEl);
      formEl.submit();
    } else {
      app.request({
        method: 'POST',
        url: '/oauth/authorize',
        body: form
      }).then((params: any) => {
        window.location.href = params.redirect;
      }).catch(() => {
        // TODO: error handling
      });
    }
  }
}
